// src/components/layout/Sidebar.tsx - Clean minimalist version
import React, {useState} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import {
    BarChart3,
    Building2,
    School,
    UserCog,
    ChevronDown,
    ChevronUp,
    Gamepad2,
    Shield,
    GraduationCap,
    Briefcase
} from 'lucide-react';

interface SidebarProps {}

// Simple, clean logo
const Logo: React.FC<{ className?: string }> = ({className = "w-8 h-8"}) => (
    <div
        className={`${className} bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center`}>
        <Gamepad2 className="w-5 h-5 text-white"/>
    </div>
);

const Sidebar: React.FC<SidebarProps> = () => {
    const location = useLocation();
    const [academicExpanded, setAcademicExpanded] = useState(false);
    const [businessExpanded, setBusinessExpanded] = useState(false);

    // Helper function to check if a route is active with specific query params
    const isRouteActive = (path: string, type?: 'academic' | 'business') => {
        // Must be on the exact path
        if (location.pathname !== path) return false;

        // For routes without type distinction (like Dashboard, Game Instances)
        if (!type) return true;

        const params = new URLSearchParams(location.search);
        const currentType = params.get('type');

        // For academic routes - only active if explicitly academic
        if (type === 'academic') {
            return currentType === 'academic';
        }

        // For business routes - only active if explicitly business
        if (type === 'business') {
            return currentType === 'business';
        }

        return false;
    };

    // Auto-expand based on current route
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const currentType = params.get('type');

        if (location.pathname.includes('/organizations') ||
            location.pathname.includes('/accounts') ||
            location.pathname.includes('/game-hosts')) {

            if (currentType === 'business') {
                setBusinessExpanded(true);
                setAcademicExpanded(false);
            } else if (currentType === 'academic') {
                setAcademicExpanded(true);
                setBusinessExpanded(false);
            } else {
                // Default to academic if no type
                setAcademicExpanded(true);
                setBusinessExpanded(false);
            }
        }
    }, [location.pathname, location.search]);

    return (
        <>
            {/* Logo Area */}
            <div
                className="fixed left-0 top-0 h-16 bg-white border-r border-gray-200 flex items-center transition-all duration-300 z-50 w-64 px-6">
                <Logo/>
                <span className="ml-3 text-lg font-semibold text-gray-900">Ron2Game Marketplace</span>
            </div>

            {/* Main Sidebar */}
            <aside
                className="fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 transition-all duration-300 z-40 w-64">

                {/* Navigation */}
                <nav className="p-4 space-y-2">

                    {/* Dashboard - Always visible, shows everything */}
                    <NavLink
                        to="/dashboard"
                        className={({isActive}) =>
                            `flex items-center p-3 rounded-lg transition-colors ${
                                isActive
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`
                        }
                    >
                        <BarChart3 className="w-5 h-5"/>
                        <span className="ml-3 font-medium">Dashboard</span>
                    </NavLink>

                    {/* Academic Section */}
                    <div>
                        <button
                            onClick={() => setAcademicExpanded(!academicExpanded)}
                            className="w-full flex items-center justify-between p-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center">
                                <GraduationCap className="w-5 h-5"/>
                                <span className="ml-3 font-medium">Academic</span>
                            </div>
                            {academicExpanded ? <ChevronUp className="w-4 h-4"/> :
                                <ChevronDown className="w-4 h-4"/>}
                        </button>

                        {academicExpanded && (
                            <div className="ml-6 mt-1 space-y-1">
                                <NavLink
                                    to="/organizations?type=academic"
                                    className={() =>
                                        `flex items-center p-2 rounded-lg transition-colors text-sm ${
                                            isRouteActive('/organizations', 'academic')
                                                ? 'bg-blue-500 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`
                                    }
                                >
                                    <Building2 className="w-4 h-4"/>
                                    <span className="ml-3">Districts</span>
                                </NavLink>

                                <NavLink
                                    to="/accounts?type=academic"
                                    className={() =>
                                        `flex items-center p-2 rounded-lg transition-colors text-sm ${
                                            isRouteActive('/accounts', 'academic')
                                                ? 'bg-blue-500 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`
                                    }
                                >
                                    <School className="w-4 h-4"/>
                                    <span className="ml-3">Schools</span>
                                </NavLink>

                                <NavLink
                                    to="/game-hosts?type=academic"
                                    className={() =>
                                        `flex items-center p-2 rounded-lg transition-colors text-sm ${
                                            isRouteActive('/game-hosts', 'academic')
                                                ? 'bg-blue-500 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`
                                    }
                                >
                                    <UserCog className="w-4 h-4"/>
                                    <span className="ml-3">Hosts</span>
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {/* Business Section */}
                    <div>
                        <button
                            onClick={() => setBusinessExpanded(!businessExpanded)}
                            className="w-full flex items-center justify-between p-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center">
                                <Briefcase className="w-5 h-5"/>
                                <span className="ml-3 font-medium">Business</span>
                            </div>
                            {businessExpanded ? <ChevronUp className="w-4 h-4"/> :
                                <ChevronDown className="w-4 h-4"/>}
                        </button>

                        {businessExpanded && (
                            <div className="ml-6 mt-1 space-y-1">
                                <NavLink
                                    to="/organizations?type=business"
                                    className={() =>
                                        `flex items-center p-2 rounded-lg transition-colors text-sm ${
                                            isRouteActive('/organizations', 'business')
                                                ? 'bg-blue-500 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`
                                    }
                                >
                                    <Building2 className="w-4 h-4"/>
                                    <span className="ml-3">Tier 1s</span>
                                </NavLink>

                                <NavLink
                                    to="/accounts?type=business"
                                    className={() =>
                                        `flex items-center p-2 rounded-lg transition-colors text-sm ${
                                            isRouteActive('/accounts', 'business')
                                                ? 'bg-blue-500 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`
                                    }
                                >
                                    <School className="w-4 h-4"/>
                                    <span className="ml-3">Tier 2s</span>
                                </NavLink>

                                <NavLink
                                    to="/game-hosts?type=business"
                                    className={() =>
                                        `flex items-center p-2 rounded-lg transition-colors text-sm ${
                                            isRouteActive('/game-hosts', 'business')
                                                ? 'bg-blue-500 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`
                                    }
                                >
                                    <UserCog className="w-4 h-4"/>
                                    <span className="ml-3">Hosts</span>
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {/* Spacer */}
                    <div className="py-2"></div>

                    {/* System Administration */}
                    <NavLink
                        to="/games"
                        className={({isActive}) =>
                            `flex items-center p-3 rounded-lg transition-colors ${
                                isActive
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`
                        }
                    >
                        <Gamepad2 className="w-5 h-5"/>
                        <span className="ml-3 font-medium">Game Instances</span>
                    </NavLink>

                    <NavLink
                        to="/admins"
                        className={({isActive}) =>
                            `flex items-center p-3 rounded-lg transition-colors ${
                                isActive
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`
                        }
                    >
                        <Shield className="w-5 h-5"/>
                        <span className="ml-3 font-medium">Manage Admins</span>
                    </NavLink>

                </nav>
            </aside>
        </>
    );
};

export default Sidebar;
